/**
 * Dossier Snapshot - Page 1 of the executive dossier.
 *
 * Contains: Photo, Identity Block, Traffic Light Grid (compact), Key Findings
 */

import { ExecutiveDetail, VerificationStatus } from './types';
import TrafficLightGrid from './TrafficLightGrid';
import KeyFindingsCards from './KeyFindingsCards';
import IntelligenceThesis from './IntelligenceThesis';

interface DossierSnapshotProps {
  executive: ExecutiveDetail;
}

// Verification status display
const verificationLabels: Record<VerificationStatus, { label: string; bg: string; text: string }> = {
  verified: { label: 'Verified', bg: 'bg-green-100', text: 'text-green-800' },
  corroborated: { label: 'Corroborated', bg: 'bg-blue-100', text: 'text-blue-800' },
  unverified: { label: 'Unverified', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  stale: { label: 'Stale', bg: 'bg-red-100', text: 'text-red-800' },
};

function VerificationBadge({ status }: { status: VerificationStatus }) {
  const style = verificationLabels[status] || verificationLabels.unverified;
  return (
    <span className={`inline-flex items-center rounded-full ${style.bg} px-2.5 py-0.5 text-xs font-medium ${style.text}`}>
      {status === 'verified' && (
        <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {style.label}
    </span>
  );
}

export function DossierSnapshot({ executive }: DossierSnapshotProps) {
  const assessment = executive.assessment;

  return (
    <div className="space-y-8">
      {/* Identity Block */}
      <div className="bg-gradient-to-r from-primary-700 to-primary-800 rounded-xl p-6 text-white">
        <div className="flex items-start gap-6">
          {/* Photo */}
          <div className="flex-shrink-0">
            {executive.photo_url ? (
              <img
                src={executive.photo_url}
                alt={executive.full_name}
                className="h-28 w-28 rounded-xl object-cover ring-4 ring-white/30"
              />
            ) : (
              <div className="h-28 w-28 rounded-xl bg-primary-500 flex items-center justify-center ring-4 ring-white/30">
                <span className="text-3xl font-bold text-white">
                  {executive.full_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
            )}
          </div>

          {/* Identity Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold">{executive.full_name}</h1>
                <p className="text-lg text-primary-200 mt-1">{executive.current_title}</p>
                {executive.current_company_name && (
                  <p className="text-primary-300 mt-1">{executive.current_company_name}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <VerificationBadge status={executive.verification_status || 'unverified'} />
                {executive.data_completeness !== undefined && (
                  <div className="text-sm text-primary-200">
                    {Math.round(executive.data_completeness * 100)}% Complete
                  </div>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="mt-4 flex items-center gap-6 text-sm text-primary-200">
              {executive.career_history && executive.career_history.length > 0 && (
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{executive.career_history.length} positions</span>
                </div>
              )}
              {executive.board_memberships && executive.board_memberships.length > 0 && (
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{executive.board_memberships.length} boards</span>
                </div>
              )}
              {executive.education && executive.education.length > 0 && (
                <div className="flex items-center gap-1">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                  </svg>
                  <span>{executive.education?.[0]?.institution}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Light Grid - Compact View */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <TrafficLightGrid
          dimensions={assessment?.dimensions || []}
          compact={false}
        />
      </div>

      {/* Key Findings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <KeyFindingsCards
          findings={assessment?.key_findings}
          keyFacts={executive.bio?.key_facts}
          careerHighlights={executive.bio?.career_highlights}
        />
      </div>

      {/* Intelligence Thesis */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <IntelligenceThesis
          thesis={assessment?.intelligence_thesis}
          synthesizedBio={executive.bio?.synthesized_bio}
          dataGaps={assessment?.data_gaps}
        />
      </div>
    </div>
  );
}

export default DossierSnapshot;
